import { useState } from 'react'

import { useLocation } from 'react-router-dom'

import HubbleHeader from '@/components/layouts/HubbleHeader'
import NavigationSidebar from '@/components/layouts/NavigationSidebar'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { useAuthStore } from '@/stores/auth.store'
import { useControlCenterStore } from '@/stores/controlCenter.store'

import StatusMessage from './components/shared/StatusMessage'
import useAdminApplications from './hooks/useAdminApplications'
import useAdminClients from './hooks/useAdminClients'
import Alerts from './Alerts'
import AlertsControls from './Alerts/AlertsControls'
import useAlertsData from './Alerts/hooks/useAlertsData'
import FeatureFlags from './FeatureFlags'
import FlagsControls from './FeatureFlags/FlagsControls'
import useFeatureFlagsData from './FeatureFlags/hooks/useFeatureFlagsData'

const ControlCenter = () => {
  const location = useLocation()

  const isFlags = location.pathname !== '/control-center/alerts'

  const {
    flagStatusFilter,
    setFlagStatusFilter,
    flagsSearchQuery,
    setFlagsSearchQuery,
    alertSeverityFilter,
    setAlertSeverityFilter,
    alertStatusFilter,
    setAlertStatusFilter,
    alertsSearchQuery,
    setAlertsSearchQuery,
  } = useControlCenterStore()

  const role = useAuthStore((s) => s.permissions?.role)
  const [isCreatingFlag, setIsCreatingFlag] = useState(false)
  const [isCreatingAlert, setIsCreatingAlert] = useState(false)
  const canEdit = role === 'SUPER_ADMIN' || role === 'ADMIN'

  const { clientNameToId } = useAdminClients()
  const { applications } = useAdminApplications()

  const { isLoading: flagsLoading, error: flagsError } = useFeatureFlagsData()
  const { isLoading: alertsLoading, error: alertsError } = useAlertsData()

  const flagsContent = flagsLoading ? (
    <StatusMessage loading />
  ) : flagsError ? (
    <StatusMessage text='Failed to load feature flags.' error />
  ) : (
    <FeatureFlags
      isCreating={isCreatingFlag}
      onCreateClose={() => setIsCreatingFlag(false)}
      clientNameToId={clientNameToId}
    />
  )

  const alertsContent = alertsLoading ? (
    <StatusMessage loading />
  ) : alertsError ? (
    <StatusMessage text='Failed to load alerts.' error />
  ) : (
    <Alerts
      applications={applications}
      clientNameToId={clientNameToId}
      isCreating={isCreatingAlert}
      onCreateClose={() => setIsCreatingAlert(false)}
    />
  )

  return (
    <div className='flex w-full flex-1 flex-col'>
      <SidebarProvider>
        <HubbleHeader />
        <NavigationSidebar className='md:top-[3rem] md:h-[calc(100vh-3rem)]' />

        <SidebarInset>
          <div className='relative mt-[3rem] flex h-[calc(100vh-3rem)] w-full flex-1 flex-col text-white'>
            <header className='border-border-shadow bg-neutral relative flex h-12 w-full max-w-full items-center border-b px-1 md:justify-between'>
              <SidebarTrigger className='z-40 my-1 hidden md:inline-flex' />
              <h1 className='mx-3 inline-flex space-x-1 text-sm font-medium'>
                <span className='text-text-tertiary'>Control Center</span>
                <span className='text-divider-main'>/</span>
                <span className='text-text-primary'>
                  {isFlags ? 'Feature Flags' : 'Alerts'}
                </span>
              </h1>
              <div className='flex h-full w-full min-w-0 flex-1 items-center' />
            </header>

            <nav className='border-divider-main bg-neutral-dimmed flex min-h-12 flex-wrap items-center justify-end gap-2 border-b px-4 py-2'>
              {isFlags ? (
                <FlagsControls
                  statusFilter={flagStatusFilter}
                  onStatusChange={setFlagStatusFilter}
                  searchQuery={flagsSearchQuery}
                  onSearchChange={setFlagsSearchQuery}
                  canEdit={canEdit}
                  onAdd={() => setIsCreatingFlag(true)}
                />
              ) : (
                <AlertsControls
                  severityFilter={alertSeverityFilter}
                  onSeverityChange={setAlertSeverityFilter}
                  statusFilter={alertStatusFilter}
                  onStatusChange={setAlertStatusFilter}
                  searchQuery={alertsSearchQuery}
                  onSearchChange={setAlertsSearchQuery}
                  canEdit={canEdit}
                  onAdd={() => setIsCreatingAlert(true)}
                />
              )}
            </nav>

            <div className='bg-neutral-dimmed flex min-h-0 flex-1 overflow-auto px-6 py-4'>
              {isFlags ? flagsContent : alertsContent}
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  )
}

export default ControlCenter
